/**
 * Trustpilot product reviews — renders from the pre-built, pre-redacted static
 * JSON asset (assets/trustpilot-reviews.json). Same-origin fetch: no API key,
 * no CORS, no Trustpilot call at render time.
 *
 * Layout (owns everything below the section heading):
 *   · Summary sidebar — average score + Trustpilot stars + count, and a
 *     rating-distribution bar row that doubles as a STAR-SCORE FILTER.
 *   · Reviews — desktop: paginated 2-col grid; mobile: horizontal scroll-snap
 *     carousel. Always sorted newest-first (no sort UI).
 *   · "Read more" opens a modal with the full review (+ reply).
 *
 * Redacted health-claim passages arrive already masked (█ runs, `redact`
 * segments); the original words are NOT in the payload. We only style them blurred.
 *
 * Host element <trustpilot-product-reviews> attributes:
 *   data-src        URL of the reviews JSON
 *   data-skus       comma-separated variant SKUs for the current product
 *   data-page-size  desktop reviews per page (default 4)
 */

// Trustpilot star tiles (from snippets/trustpilot-star-*.liquid).
const STAR_FULL =
  '<svg class="tpr-star" viewBox="0 0 27 28" width="20" height="20" aria-hidden="true"><path d="M0.813 27.475H26.854V0.724H0.813V27.475Z" fill="#00B67A"/><path d="M23.012 12.106L8.242 23.128l2.155-6.812-5.642-4.21h6.973l2.155-6.812 2.155 6.812h6.973ZM13.884 18.918l4.034-.872 1.607 5.082-5.641-4.21Z" fill="#fff"/></svg>';
const STAR_HALF =
  '<svg class="tpr-star" viewBox="0 0 27 28" width="20" height="20" aria-hidden="true"><path d="M0.813 27.475H26.854V0.724H0.813V27.475Z" fill="#c8d3cf"/><path d="M0.813 27.475H13.834V0.724H0.813V27.475Z" fill="#00B67A"/><path d="M23.012 12.106L8.242 23.128l2.155-6.812-5.642-4.21h6.973l2.155-6.812 2.155 6.812h6.973ZM13.884 18.918l4.034-.872 1.607 5.082-5.641-4.21Z" fill="#fff"/></svg>';
const STAR_EMPTY =
  '<svg class="tpr-star" viewBox="0 0 27 28" width="20" height="20" aria-hidden="true"><path d="M0.813 27.475H26.854V0.724H0.813V27.475Z" fill="#c8d3cf"/><path d="M23.012 12.106L8.242 23.128l2.155-6.812-5.642-4.21h6.973l2.155-6.812 2.155 6.812h6.973ZM13.884 18.918l4.034-.872 1.607 5.082-5.641-4.21Z" fill="#fff"/></svg>';

const DESKTOP_MQ = '(min-width: 1000px)';
const MOBILE_CAROUSEL_LIMIT = 50;
const MAX_PAGE_BUTTONS = 6;

function starRow(value, size) {
  const rounded = Math.round(value * 2) / 2;
  let html = '<span class="tpr-stars' + (size ? ' tpr-stars--' + size : '') + '">';
  for (let i = 1; i <= 5; i++) {
    if (rounded >= i) html += STAR_FULL;
    else if (rounded >= i - 0.5) html += STAR_HALF;
    else html += STAR_EMPTY;
  }
  return html + '</span>';
}

class TrustpilotProductReviews extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.root = this.querySelector('[data-tpr-root]');
    this.pageSize = parseInt(this.getAttribute('data-page-size') || '4', 10);
    this.locale = this.getAttribute('data-locale') || 'nl';
    this.i18n = Object.assign({
      basedOn: 'op basis van {count} reviews',
      replyLabel: 'Antwoord van plnktn',
      readMore: 'Lees meer',
      showAll: 'Toon alle reviews',
      empty: 'Geen reviews met deze score.',
      close: 'Sluiten',
      filterAria: 'Filter op score',
      paginationAria: 'Reviews-paginering',
      starsAria: '{count} van 5 sterren',
    }, this.readI18n());
    this.filter = 'all';
    this.page = 1;
    this.reviews = [];
    this.summary = null;
    this.mq = window.matchMedia(DESKTOP_MQ);
    this.isDesktop = this.mq.matches;
    this.mq.addEventListener('change', (e) => { this.isDesktop = e.matches; this.page = 1; this.render(); });
    this.load();
  }

  readI18n() {
    try { return JSON.parse(this.querySelector('.tpr-i18n')?.textContent || '{}'); }
    catch { return {}; }
  }

  async load() {
    try {
      const res = await fetch(this.getAttribute('data-src'), { cache: 'no-cache' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const product = this.pickProduct(data.products || {});
      // Only show when the product has at least 3 reviews.
      if (!product || !product.reviews || product.reviews.length < 3) return this.empty();
      // Always newest-first.
      this.reviews = product.reviews.slice().sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
      this.summary = product.summary || this.deriveSummary();
      // Reviews hidden by id in the theme settings are left out of the list only:
      // the summary above (average, count, star bars) still counts them.
      const hiddenIds = this.hiddenIds();
      if (hiddenIds.size) this.reviews = this.reviews.filter((r) => !hiddenIds.has(String(r.id)));
      this.render();
      this.removeAttribute('hidden');
    } catch (e) {
      this.empty();
      if (window.Shopify && Shopify.designMode) console.warn('[tpr]', e);
    }
  }

  // Review ids from the theme setting, separated by commas, spaces or new lines.
  hiddenIds() {
    return new Set((this.getAttribute('data-hidden-ids') || '').split(/[\s,;]+/).map((id) => id.trim()).filter(Boolean));
  }

  pickProduct(products) {
    const skus = (this.getAttribute('data-skus') || '').split(',').map((s) => s.trim()).filter(Boolean);
    const entries = Object.values(products);
    for (const sku of skus) {
      if (products[sku]) return products[sku];
      // explicit group membership (a product groups several variant SKUs)
      const g = entries.find((p) => Array.isArray(p.skus) && p.skus.includes(sku));
      if (g) return g;
      // fall back to the base SKU (variant suffixes like -120 share it)
      const base = sku.split('-')[0];
      if (products[base]) return products[base];
    }
    return entries.length === 1 ? entries[0] : null;
  }

  deriveSummary() {
    const stars = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    this.reviews.forEach((r) => { stars[r.stars] = (stars[r.stars] || 0) + 1; sum += r.stars; });
    return { total: this.reviews.length, average: this.reviews.length ? sum / this.reviews.length : 0, stars };
  }

  filtered() {
    if (this.filter !== 'all') return this.reviews.filter((r) => r.stars === this.filter);
    // Default "all" view: 4- & 5-star first (fill the early pages), then 3/2/1.
    // Each group keeps its newest-first order; 1/2/3-star stay reachable via the filter.
    const hi = this.reviews.filter((r) => r.stars >= 4);
    const lo = this.reviews.filter((r) => r.stars < 4);
    return [...hi, ...lo];
  }

  render() {
    // A viewport change can arrive before the reviews request has completed.
    if (!this.summary) return;
    this.root.innerHTML = '';
    this.root.className = 'tpr__body' + (this.isDesktop ? ' is-desktop' : ' is-mobile');
    this.root.append(this.buildSummary());
    this.root.append(this.buildReviews());
  }

  // ---- summary sidebar (with rating-bar score filter) ---------------------
  buildSummary() {
    const s = this.summary;
    const aside = document.createElement('aside');
    aside.className = 'tpr__summary';

    const score = document.createElement('div');
    score.className = 'tpr__score';
    score.innerHTML =
      '<span class="tpr__score-num">' + this.fmtNum(s.average) + ' / 5</span>' +
      starRow(s.average, 'md') +
      '<span class="tpr__score-count">' + this.i18n.basedOn.replace('{count}', '<strong>' + s.total + '</strong>') + '</span>';
    aside.append(score);

    const bars = document.createElement('div');
    bars.className = 'tpr__bars';
    bars.setAttribute('role', 'toolbar');
    bars.setAttribute('aria-label', this.i18n.filterAria);
    [5, 4, 3, 2, 1].forEach((star) => {
      const count = (s.stars && (s.stars[star] || s.stars[String(star)])) || 0;
      const pct = s.total > 0 ? (count / s.total) * 100 : 0;
      const active = this.filter === star;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tpr__bar' + (active ? ' is-active' : '') + (count === 0 ? ' is-empty' : '');
      btn.disabled = count === 0;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.innerHTML =
        '<span class="tpr__bar-label">' + star + '★</span>' +
        '<span class="tpr__bar-track"><span class="tpr__bar-fill" style="width:' + pct + '%"></span></span>' +
        '<span class="tpr__bar-count">' + count + '</span>';
      btn.addEventListener('click', () => {
        this.filter = active ? 'all' : star;
        this.page = 1;
        this.render();
        this.scrollToTop();
      });
      bars.append(btn);
    });
    aside.append(bars);

    if (this.filter !== 'all') {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'tpr__clear';
      clear.textContent = this.i18n.showAll;
      clear.addEventListener('click', () => { this.filter = 'all'; this.page = 1; this.render(); this.scrollToTop(); });
      aside.append(clear);
    }
    return aside;
  }

  // ---- reviews (grid + pagination / carousel) -----------------------------
  buildReviews() {
    const wrap = document.createElement('div');
    wrap.className = 'tpr__reviews';
    const list = this.filtered();

    if (!list.length) {
      const empty = document.createElement('p');
      empty.className = 'tpr__empty';
      empty.textContent = this.i18n.empty;
      wrap.append(empty);
      return wrap;
    }

    const ul = document.createElement('ul');

    if (this.isDesktop) {
      ul.className = 'tpr__grid';
      const totalPages = Math.max(1, Math.ceil(list.length / this.pageSize));
      this.page = Math.min(this.page, totalPages);
      const start = (this.page - 1) * this.pageSize;
      list.slice(start, start + this.pageSize).forEach((r) => ul.append(this.card(r, false)));
      wrap.append(ul);
      if (totalPages > 1) wrap.append(this.pagination(totalPages));
    } else {
      ul.className = 'tpr__carousel';
      list.slice(0, MOBILE_CAROUSEL_LIMIT).forEach((r) => ul.append(this.card(r, true)));
      wrap.append(ul);
    }
    return wrap;
  }

  pagination(totalPages) {
    const nav = document.createElement('nav');
    nav.className = 'tpr__pagination';
    nav.setAttribute('aria-label', this.i18n.paginationAria);
    const mk = (label, page, opts = {}) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tpr__page' + (opts.active ? ' is-active' : '');
      b.textContent = label;
      b.disabled = !!opts.disabled;
      if (!opts.disabled && page) b.addEventListener('click', () => { this.page = page; this.render(); this.scrollToTop(); });
      return b;
    };
    nav.append(mk('←', this.page - 1, { disabled: this.page === 1 }));
    buildPageList(this.page, totalPages, MAX_PAGE_BUTTONS).forEach((p) => {
      if (p === '…') {
        const span = document.createElement('span');
        span.className = 'tpr__ellipsis';
        span.textContent = '…';
        nav.append(span);
      } else {
        nav.append(mk(String(p), p, { active: p === this.page }));
      }
    });
    nav.append(mk('→', this.page + 1, { disabled: this.page === totalPages }));
    return nav;
  }

  card(r, truncate) {
    const li = document.createElement('li');
    li.className = 'tpr-card' + (truncate ? ' tpr-card--fixed' : '');
    if (r.id != null) li.setAttribute('data-review-id', r.id);

    const head = document.createElement('div');
    head.className = 'tpr-card__head';
    head.innerHTML = starRow(r.stars, 'sm') +
      '<time class="tpr-card__date">' + this.fmtDate(r.createdAt) + '</time>';
    const starsEl = head.querySelector('.tpr-stars');
    if (starsEl) {
      starsEl.setAttribute('role', 'img');
      starsEl.setAttribute('aria-label', this.i18n.starsAria.replace('{count}', r.stars));
    }

    const body = document.createElement('p');
    body.className = 'tpr-card__body' + (truncate ? ' tpr-card__body--clamp' : '');
    body.append(...this.segments(r.segments));

    const author = document.createElement('div');
    author.className = 'tpr-card__author';
    author.textContent = r.author;

    li.append(head, body);

    // Body "read more" (mobile / long reviews) → modal with full content.
    const textLen = (r.segments || []).reduce((n, s) => n + s.v.length, 0);
    if (truncate && textLen > 160) li.append(this.moreButton(r));

    li.append(author);

    if (r.replySegments && r.replySegments.length) {
      const reply = document.createElement('div');
      reply.className = 'tpr-card__reply';
      const label = document.createElement('span');
      label.className = 'tpr-card__reply-label';
      label.textContent = this.i18n.replyLabel;
      const rb = document.createElement('p');
      rb.className = 'tpr-card__reply-body--clamp'; // replies clamp to 4 lines on all breakpoints
      rb.append(...this.segments(r.replySegments));
      reply.append(label, rb);
      li.append(reply);
      // Desktop: give clamped replies their own "read more" (on mobile the
      // card-level button already opens the modal with the full reply).
      const replyLen = r.replySegments.reduce((n, s) => n + s.v.length, 0);
      if (!truncate && replyLen > 220) reply.append(this.moreButton(r));
    }
    return li;
  }

  moreButton(r) {
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'tpr-card__more';
    more.textContent = this.i18n.readMore;
    more.addEventListener('click', () => this.openModal(r));
    return more;
  }

  openModal(r) {
    const back = document.createElement('div');
    back.className = 'tpr-modal';
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', this.i18n.readMore + ' — ' + r.author);
    const returnFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    let closed = false;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const close = () => {
      if (closed) return;
      closed = true;
      document.removeEventListener('keydown', onKey);
      back.remove();
      document.body.style.overflow = previousOverflow;
      removeTrapFocus(returnFocus?.isConnected ? returnFocus : null);
    };

    const panel = document.createElement('div');
    panel.className = 'tpr-modal__panel';
    panel.addEventListener('click', (e) => e.stopPropagation());

    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'tpr-modal__close';
    x.setAttribute('aria-label', this.i18n.close);
    x.innerHTML = '&times;';
    x.addEventListener('click', close);

    const head = document.createElement('div');
    head.className = 'tpr-modal__head';
    head.innerHTML = starRow(r.stars, 'md') +
      '<span class="tpr-modal__meta"><strong>' + escapeHtml(r.author) + '</strong> · ' + this.fmtDate(r.createdAt) + '</span>';

    const body = document.createElement('p');
    body.className = 'tpr-modal__body';
    body.append(...this.segments(r.segments));

    panel.append(x, head, body);

    if (r.replySegments && r.replySegments.length) {
      const reply = document.createElement('div');
      reply.className = 'tpr-modal__reply';
      const label = document.createElement('span');
      label.className = 'tpr-card__reply-label';
      label.textContent = this.i18n.replyLabel;
      const rb = document.createElement('p');
      rb.append(...this.segments(r.replySegments));
      reply.append(label, rb);
      panel.append(reply);
    }

    back.append(panel);
    back.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    document.body.append(back);
    trapFocus(back, x);
  }

  segments(segs) {
    return (segs || []).map((s) => {
      if (s.t === 'redact') {
        const span = document.createElement('span');
        span.className = 'tpr-redact';
        span.textContent = s.v;
        span.setAttribute('aria-hidden', 'true');
        span.title = 'Deze passage is verborgen';
        return span;
      }
      return document.createTextNode(s.v);
    });
  }

  fmtDate(iso) {
    try { return new Date(iso).toLocaleDateString(this.locale, { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  }

  fmtNum(n) {
    const v = Math.round(n * 10) / 10;
    try { return v.toLocaleString(this.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }); }
    catch { return String(v).replace('.', ','); }
  }

  scrollToTop() {
    const h = this.querySelector('.tpr__head');
    if (h) h.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  empty() { this.setAttribute('hidden', ''); }
}

function buildPageList(current, total, maxButtons) {
  if (total <= maxButtons) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= maxButtons - 3) {
    const head = Array.from({ length: maxButtons - 2 }, (_, i) => i + 1);
    return [...head, '…', total];
  }
  if (current >= total - (maxButtons - 4)) {
    const tail = Array.from({ length: maxButtons - 2 }, (_, i) => total - (maxButtons - 3) + i);
    return [1, '…', ...tail];
  }
  return [1, '…', current - 1, current, current + 1, '…', total];
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

customElements.define('trustpilot-product-reviews', TrustpilotProductReviews);
